import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addToCart } from '../redux/cartSlice';
import { productService } from '../services/api';
import { Container, Row, Col, Button, Spinner, Alert, Image, Card, Badge, Tabs, Tab } from 'react-bootstrap';
import { FaStar, FaHeart, FaShoppingCart, FaShare } from 'react-icons/fa';

const ProductDetailPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await productService.getProductById(id);
        setProduct(response.data);
      
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError('Failed to load product details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (product) {
      dispatch(addToCart({
        id: product.id,
        title: product.title,
        price: product.price,
        image: product.image,
        quantity: quantity
      }));
    }
  };

  const incrementQuantity = () => {
    if (quantity < (product?.stock || 10)) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Product not found.</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/">Home</Link></li>
              <li className="breadcrumb-item"><Link to="/products">Products</Link></li>
              <li className="breadcrumb-item active" aria-current="page">{product.title}</li>
            </ol>
          </nav>
        </Col>
      </Row>

      <Row>
        {/* Product Image */}
        <Col md={6} className="mb-4">
          <Image 
            src={product.image} 
            alt={product.title} 
            className="img-fluid rounded" 
            style={{ maxHeight: '500px', width: '100%', objectFit: 'contain' }}
          />
          
          <Row className="mt-3">
            {/* Thumbnail images would go here */}
            {[1].map((i) => (
              <Col key={i} xs={3}>
                <Image 
                  src={product.image_url} 
                  alt={`Thumbnail ${i}`}
                  className="img-thumbnail" 
                  style={{ cursor: 'pointer' }}
                />
              </Col>
            ))}
          </Row>
        </Col>

        {/* Product Info */}
        <Col md={6}>
          <h1 className="mb-2">{product.title}</h1>
          
          <div className="d-flex align-items-center mb-3">
            <div className="text-warning me-2">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className={i < Math.round(product.rating || 4) ? "text-warning" : "text-muted"} />
              ))}
            </div>
            <span className="text-muted">({product.numReviews || 24} reviews)</span>
          </div>
          
          <h2 className="h3 text-primary mb-4">₹{(typeof product.price === 'number' ? product.price : parseFloat(product.price || 0)).toFixed(2)}</h2>
          
          <p className="mb-4">{product.description}</p>
          
          {product.stock > 0 ? (
            <Badge bg="success" className="mb-3">In Stock</Badge>
          ) : (
            <Badge bg="danger" className="mb-3">Out of Stock</Badge>
          )}
          
          <div className="d-flex align-items-center mb-4">
            <div className="input-group me-3" style={{ width: '130px' }}>
              <Button 
                variant="outline-secondary" 
                onClick={decrementQuantity}
                disabled={quantity <= 1}
              >-</Button>
              <input 
                type="text" 
                className="form-control text-center" 
                value={quantity} 
                readOnly
              />
              <Button 
                variant="outline-secondary" 
                onClick={incrementQuantity}
                disabled={quantity >= product.stock}
              >+</Button>
            </div>
            
            <Button 
              variant="primary" 
              className="me-2"
              onClick={handleAddToCart}
              disabled={!product.stock}
            >
              <FaShoppingCart className="me-2" /> Add to Cart
            </Button>
            
            <Button variant="outline-danger">
              <FaHeart />
            </Button>
          </div>
          
          <hr />
          
          <div className="mb-3">
            <strong>SKU:</strong> {product.sku || 'PRD-' + product.id.toString().padStart(6, '0')}
          </div>
          
          <div className="mb-3">
            <strong>Category:</strong> {product.category_name || 'Electronics'}
          </div>
          
          <div className="mb-4">
            <strong>Tags:</strong> {' '}
            {(product.tags || ['Electronics', 'Gadgets', 'Tech']).map((tag, index) => (
              <Badge key={index} bg="secondary" className="me-1">{tag}</Badge>
            ))}
          </div>
          
          <div className="d-flex">
            <Button variant="outline-secondary" size="sm" className="me-2">
              <FaShare className="me-1" /> Share
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <Tabs defaultActiveKey="description" className="mb-3">
            <Tab eventKey="description" title="Description">
              <Card.Body>
                <p>{product.description}</p>
                <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla quam velit, vulputate eu pharetra nec, mattis ac neque. Duis vulputate commodo lectus, ac blandit elit tincidunt id.</p>
              </Card.Body>
            </Tab>
            <Tab eventKey="details" title="Additional Information">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <dl className="row">
                      <dt className="col-sm-4">Weight</dt>
                      <dd className="col-sm-8">{product.weight || '0.5'} kg</dd>
                      
                      <dt className="col-sm-4">Dimensions</dt>
                      <dd className="col-sm-8">{product.dimensions || '10 × 10 × 3'} cm</dd>
                      
                      <dt className="col-sm-4">Material</dt>
                      <dd className="col-sm-8">{product.material || 'Plastic, Metal'}</dd>
                    </dl>
                  </Col>
                  <Col md={6}>
                    <dl className="row">
                      <dt className="col-sm-4">Brand</dt>
                      <dd className="col-sm-8">{product.brand || 'TechBrand'}</dd>
                      
                      <dt className="col-sm-4">Color</dt>
                      <dd className="col-sm-8">{product.color || 'Black'}</dd>
                      
                      <dt className="col-sm-4">Warranty</dt>
                      <dd className="col-sm-8">{product.warranty || '1 year'}</dd>
                    </dl>
                  </Col>
                </Row>
              </Card.Body>
            </Tab>
            <Tab eventKey="reviews" title="Reviews">
              <Card.Body>
                <h3 className="h4 mb-4">Customer Reviews</h3>
                {/* Placeholder for reviews */}
                <Alert variant="info">No reviews yet for this product.</Alert>
              </Card.Body>
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export default ProductDetailPage; 